export interface IVector2 {
	x: number;
	y: number;
}

export class Vector2 implements IVector2 {
	public static equals(v1: IVector2, v2: IVector2) {
		return v1.x === v2.x && v1.y === v2.y;
	}

	public static from(xOrVec: number | IVector2, y?: number): Vector2 {
		const val = Vector2.safeguard(xOrVec, y);
		return val ? new Vector2(val.x, val.y) : new Vector2();
	}

	public static fromAngle(angle: number): Vector2 {
		return Vector2.fromRadian((angle / 180) * Math.PI);
	}

	public static fromRadian(radian: number): Vector2 {
		return new Vector2(Math.cos(radian), Math.sin(radian));
	}

	public static diff(v1: IVector2, v2: IVector2) {
		return new Vector2(v1.x - v2.x, v1.y - v2.y);
	}

	public static scale(v1: IVector2, scale: number) {
		return new Vector2(v1.x * scale, v1.y * scale);
	}

	public static distance(v1: IVector2, v2: IVector2) {
		return Vector2.from(v1).dist(v2);
	}

	public static dot(v1: IVector2, v2: IVector2) {
		return v1.x * v2.x + v1.y * v2.y;
	}

	public static magnitude(vector: IVector2): number {
		return Math.sqrt(vector.x * vector.x + vector.y * vector.y);
	}

	public static clampMagnitude(vector: IVector2, min: number, max: number): IVector2 {
		const magnitude = Vector2.magnitude(vector);
		let newMagnitude = magnitude;

		if (magnitude < min) {
			newMagnitude = min;
		} else if (magnitude > max) {
			newMagnitude = max;
		} else {
			// The magnitude is withing the bounds, just return a copy of the vector.
			return { x: vector.x, y: vector.y };
		}

		return {
			x: (vector.x / magnitude) * newMagnitude,
			y: (vector.y / magnitude) * newMagnitude
		};
	}

	public static normalize(v: IVector2) {
		return Vector2.from(v).normalize();
	}

	public x: number;
	public y: number;

	constructor(x?: number, y?: number) {
		this.x = x || 0;
		this.y = y || 0;
	}

	public set(vec: IVector2) {
		this.x = vec.x;
		this.y = vec.y;
		return this;
	}

	public scale(xOrVec: number | IVector2, y?: number) {
		this.x *= Vector2.safeguard(xOrVec, y).x;
		this.y *= Vector2.safeguard(xOrVec, y).y;
		return this;
	}

	public divide(xOrVec: number | IVector2, y?: number) {
		this.x /= Vector2.safeguard(xOrVec, y).x;
		this.y /= Vector2.safeguard(xOrVec, y).y;
		return this;
	}

	public add(xOrVec: number | IVector2, y?: number) {
		this.x += Vector2.safeguard(xOrVec, y).x;
		this.y += Vector2.safeguard(xOrVec, y).y;
		return this;
	}

	public sub(xOrVec: number | IVector2, y?: number) {
		this.x -= Vector2.safeguard(xOrVec, y).x;
		this.y -= Vector2.safeguard(xOrVec, y).y;
		return this;
	}

	public dist(v: IVector2) {
		const dx = this.x - v.x;
		const dy = this.y - v.y;
		return Math.sqrt(dx * dx + dy * dy);
	}

	public radian(v: IVector2): number {
		return Math.atan2(v.y - this.y, v.x - this.x);
	}

	public angle(v: IVector2): number {
		return (Math.atan2(v.y - this.y, v.x - this.x) * 180) / Math.PI;
	}

	public normalize() {
		const m = this.magnitude();
		if (m > 0) {
			this.divide(m);
		}
		return this;
	}

	public magnitude() {
		return Vector2.magnitude(this);
	}

	public clone() {
		return new Vector2(this.x, this.y);
	}

	public lerpTowards(v: Vector2, amount: number = 0.5) {
		const delta = Vector2.sub(v, this);
		delta.scale(amount);
		this.add(delta);
	}

	/**
	 * Helpers
	 **/
	private static safeguard(value: IVector2 | number, y?: number): IVector2 {
		return typeof value === 'number' ? { x: value, y: y ?? value } : value;
	}

	public toString() {
		return '[ ' + this.x + ', ' + this.y + ' ]';
	}

	/**
	 * Static
	 **/
	public static sub(v1: IVector2, v2: IVector2): Vector2 {
		return new Vector2(v1.x - v2.x, v1.y - v2.y);
	}

	public static add(v1: IVector2, v2: IVector2): Vector2 {
		return new Vector2(v1.x + v2.x, v1.y + v2.y);
	}
}
